import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useT } from '../i18n'
import {
  cacheMatrix,
  cloneMatrix,
  getDefaultMatrixForSpecies,
  interpolateSfr,
  loadCachedMatrix,
  lookupSfrValue,
  markLocalSfrMatrixEdit
} from '../utils/sfrMatrix'
import { loadSfrMatrix, retrySfrMatrixSync, saveSfrMatrix } from '../utils/sfrMatrixApi'
import MatrixTable, { type MatrixData } from './MatrixTable'

const SAVE_DEBOUNCE_MS = 600

type SFRMatrixProps = {
  onSFRChange?: (sfrData: MatrixData) => void
  onStatusChange?: (status: SfrMatrixStatusPresentation | null) => void
  species?: string
  refreshKey?: number
  externalMatrix?: MatrixData
  externalMatrixVersion?: number
  persistenceMode?: 'species' | 'external'
}

type SaveStatus = 'loading' | 'saving' | 'refreshing' | 'error' | 'offline' | 'pending' | 'synced'

export type SfrMatrixStatusTone = 'info' | 'success' | 'error'

export type SfrMatrixStatusPresentation = {
  state: SaveStatus
  tone: SfrMatrixStatusTone
  message: string
}

const statusBannerStyle = (tone: SfrMatrixStatusTone): React.CSSProperties => ({
  marginBottom: '16px',
  padding: '12px 16px',
  borderRadius: '10px',
  border: tone === 'success' ? '1px solid #86efac' : tone === 'error' ? '1px solid #fecaca' : '1px solid #bae6fd',
  background: tone === 'success' ? '#f0fdf4' : tone === 'error' ? '#fef2f2' : '#f0f9ff',
  color: tone === 'success' ? '#166534' : tone === 'error' ? '#b91c1c' : '#075985',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '1.5'
})

export const getSfrMatrixStatusPresentation = (
  status: SaveStatus,
  translate: (key: string) => string,
  errorMessage: string | null
): SfrMatrixStatusPresentation | null => {
  switch (status) {
    case 'loading':
      return {
        state: status,
        tone: 'info',
        message: translate('sfrManagement.status.loading')
      }
    case 'saving':
      return {
        state: status,
        tone: 'info',
        message: translate('sfrManagement.status.saving')
      }
    case 'refreshing':
      return {
        state: status,
        tone: 'info',
        message: translate('sfrManagement.status.refreshing')
      }
    case 'offline':
      return {
        state: status,
        tone: 'info',
        message: translate('sfrManagement.status.offline')
      }
    case 'pending':
      return {
        state: status,
        tone: 'info',
        message: translate('sfrManagement.status.pending')
      }
    case 'synced':
      return {
        state: status,
        tone: 'success',
        message: translate('sfrManagement.status.synced')
      }
    case 'error':
      return {
        state: status,
        tone: 'error',
        message: errorMessage ?? translate('sfrManagement.status.refreshFailed')
      }
    default:
      return null
  }
}

const SFRMatrix: React.FC<SFRMatrixProps> = ({
  onSFRChange,
  onStatusChange,
  species = 'global',
  refreshKey,
  externalMatrix,
  externalMatrixVersion,
  persistenceMode = 'species'
}) => {
  const t = useT()
  const [sfrData, setSfrData] = useState<MatrixData>(() => getDefaultMatrixForSpecies(species))
  const [status, setStatus] = useState<SaveStatus>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const skipNextSaveRef = useRef(true)
  const onSFRChangeRef = useRef<typeof onSFRChange>()
  const refreshKeyRef = useRef(refreshKey)
  const statusVersionRef = useRef(0)

  useEffect(() => {
    onSFRChangeRef.current = onSFRChange
  }, [onSFRChange])

  useEffect(() => {
    if (!externalMatrix) return
    skipNextSaveRef.current = false
    const cloned = cloneMatrix(externalMatrix)
    setSfrData(cloned)
  }, [externalMatrixVersion, externalMatrix, species])

  useEffect(() => {
    if (persistenceMode === 'external') {
      statusVersionRef.current += 1
      setStatus('synced')
      setErrorMessage(null)

      const nextMatrix = cloneMatrix(externalMatrix ?? getDefaultMatrixForSpecies(species))
      skipNextSaveRef.current = true
      setSfrData(nextMatrix)
      return
    }

    skipNextSaveRef.current = true
    const forceRefresh = refreshKey !== refreshKeyRef.current
    refreshKeyRef.current = refreshKey

    statusVersionRef.current += 1
    setStatus(forceRefresh ? 'refreshing' : 'loading')
    setErrorMessage(null)

    let cancelled = false

    const localMatrix = cloneMatrix(loadCachedMatrix(species) ?? getDefaultMatrixForSpecies(species))
    skipNextSaveRef.current = true
    setSfrData(localMatrix)
    onSFRChangeRef.current?.(localMatrix)

    const load = async () => {
      const result = await loadSfrMatrix(species, { forceRefresh })

      if (cancelled) {
        return
      }

      skipNextSaveRef.current = true
      const cloned = cloneMatrix(result.matrix)
      setSfrData(cloned)
      onSFRChangeRef.current?.(cloned)

      if (result.errorMessage) {
        console.error('Failed to load SFR matrix from API', result.errorMessage)
        setStatus('error')
        setErrorMessage(t('sfrManagement.status.refreshFailed'))
        return
      }

      setStatus(result.syncState)
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [externalMatrix, persistenceMode, refreshKey, species, t])

  useEffect(() => () => {
    if (saveTimeoutRef.current) {
      globalThis.clearTimeout(saveTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (persistenceMode === 'external') {
      return
    }

    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false
      return
    }

    if (saveTimeoutRef.current) {
      globalThis.clearTimeout(saveTimeoutRef.current)
    }

    setStatus('saving')
    setErrorMessage(null)

    saveTimeoutRef.current = globalThis.setTimeout(async () => {
      const statusVersion = statusVersionRef.current + 1
      statusVersionRef.current = statusVersion

      try {
        const result = await saveSfrMatrix(species, sfrData)

        if (statusVersionRef.current !== statusVersion) {
          return
        }

        setStatus(result.syncState)

        if (!result.syncPromise) {
          return
        }

        void result.syncPromise.then((nextStatus) => {
          if (statusVersionRef.current !== statusVersion) {
            return
          }
          setStatus(nextStatus)
        })
      } catch (error) {
        console.error('Failed to save SFR matrix', error)

        if (statusVersionRef.current !== statusVersion) {
          return
        }

        setStatus('error')
        setErrorMessage(t('sfrManagement.status.saveFailed'))
      }
    }, SAVE_DEBOUNCE_MS)

    return () => {
      if (saveTimeoutRef.current) {
        globalThis.clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [persistenceMode, sfrData, species, t])

  useEffect(() => {
    if (persistenceMode === 'external') {
      return
    }

    if (status !== 'offline' && status !== 'pending') {
      return
    }

    let cancelled = false

    const retrySync = async () => {
      const nextSyncState = await retrySfrMatrixSync(species)

      if (cancelled) {
        return
      }

      if (nextSyncState !== 'synced') {
        setStatus(nextSyncState)
        return
      }

      statusVersionRef.current += 1
      setStatus('refreshing')
      setErrorMessage(null)

      const result = await loadSfrMatrix(species, { forceRefresh: true })

      if (cancelled) {
        return
      }

      skipNextSaveRef.current = true
      const cloned = cloneMatrix(result.matrix)
      setSfrData(cloned)
      onSFRChangeRef.current?.(cloned)

      if (result.errorMessage) {
        setStatus('error')
        setErrorMessage(t('sfrManagement.status.refreshFailed'))
        return
      }

      setStatus(result.syncState)
    }

    const handleOnline = () => {
      void retrySync()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      void retrySync()
    }

    const handleFocus = () => {
      void retrySync()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    void retrySync()

    return () => {
      cancelled = true
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [persistenceMode, species, status, t])

  const statusPresentation = useMemo(
    () => persistenceMode === 'external'
      ? null
      : getSfrMatrixStatusPresentation(status, (key) => t(key as any), errorMessage),
    [errorMessage, persistenceMode, status, t]
  )

  useEffect(() => {
    if (persistenceMode === 'external') {
      return
    }

    onStatusChange?.(statusPresentation)
  }, [onStatusChange, persistenceMode, statusPresentation])

  const handleDataChange = (nextData: MatrixData) => {
    skipNextSaveRef.current = false
    const cloned = cloneMatrix(nextData)
    if (persistenceMode === 'species') {
      cacheMatrix(species, cloned)
      markLocalSfrMatrixEdit(species)
    }
    setSfrData(cloned)
    onSFRChangeRef.current?.(cloned)
  }

  return (
    <div>
      {!onStatusChange && statusPresentation && (
        <div aria-live="polite" role="status" style={statusBannerStyle(statusPresentation.tone)}>
          {statusPresentation.message}
        </div>
      )}
      <MatrixTable
        title="SFR (Specific Feeding Rate) Matrix"
        data={sfrData}
        onDataChange={handleDataChange}
        rowLabel="Weight"
        columnLabel="Temperature"
        valueLabel="SFR (%)"
        editable={true}
        precision={2}
      />
    </div>
  )
}

// Utility function to lookup SFR value
export const lookupSFR = (weight: number, temperature: number, sfrData: MatrixData): number | null => {
  return lookupSfrValue(sfrData, weight, temperature)
}

// Utility function to interpolate SFR value (more precise)
export const interpolateSFR = (
  weight: number,
  temperature: number,
  sfrData: MatrixData,
  species = 'global'
): number | null => {
  const result = interpolateSfr(weight, temperature, { species, matrix: sfrData })
  return Number.isFinite(result.sfr) ? result.sfr : null
}

export default SFRMatrix